import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, Lock, Download, MessageSquare, ShieldAlert, Loader2, AudioWaveform, Sparkles, ChevronLeft, ChevronRight, PenLine, Music } from "lucide-react";
import { toast } from "sonner";
import { api, siteDownloadUrl } from "@/lib/api";
import { PlaylistRow } from "@/components/PlaylistRow";
import { OfferStackCard } from "@/components/OfferStackCard";
import { SiteFooter } from "@/components/SiteFooter";
import { Vinyl } from "@/components/Vinyl";

const BATCH_NAMES = ["Batch One", "Batch Two", "Batch Three", "Batch Four", "Batch Five", "Batch Six"];

export default function JinglePreview() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [site, setSite] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null); // no auto-pick — the visitor chooses
  const [extraTakes, setExtraTakes] = useState([]); // "Add for $17" bonus takes
  const [bump, setBump] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showLyricEditor, setShowLyricEditor] = useState(false);
  const [showDirectionEditor, setShowDirectionEditor] = useState(false);
  const [lyricsDraft, setLyricsDraft] = useState("");
  const [cutting, setCutting] = useState(false);
  const [activeBatch, setActiveBatch] = useState(0);
  const offerRef = useRef(null);
  const draftSource = useRef(""); // lyric version the editor draft was prefilled from

  const fetchSite = async (siteKey) => {
    const { data } = await api.get(`/sites/${siteKey}`);
    setSite(data);
    if (data.paid) setSelected(data.selected_take); // post-purchase: show what they bought
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: jobData } = await api.get(`/jingles/${jobId}`);
        setJob(jobData);
        if (jobData.site_key) await fetchSite(jobData.site_key);
      } catch (e) {
        setError(true);
      }
    })();
  }, [jobId]);

  // Keep polling while any batch is still recording
  useEffect(() => {
    if (!site || !site.batches.some((b) => b.status === "recording")) return;
    const t = setInterval(() => fetchSite(site.site_key).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [site?.site_key, site?.batches?.some((b) => b.status === "recording")]);

  // Jump to the newest batch whenever one lands
  const batchCount = site?.batches?.length ?? 0;
  useEffect(() => {
    if (batchCount > 0) setActiveBatch(batchCount - 1);
  }, [batchCount]);

  // Lyric versions follow the batch carousel. An untouched editor draft
  // re-prefills when the version changes; edits in progress are kept.
  useEffect(() => {
    const bs = site?.batches ?? [];
    const lyr = bs.length
      ? (bs[Math.min(activeBatch, bs.length - 1)]?.lyrics ?? site?.lyrics ?? job?.lyrics ?? [])
      : (site?.lyrics ?? job?.lyrics ?? []);
    const text = lyr.join("\n").trim();
    if (!text) return;
    setLyricsDraft((d) => {
      if (!d.trim() || d.trim() === draftSource.current) {
        draftSource.current = text;
        return text;
      }
      return d;
    });
  }, [site, job, activeBatch]);

  const select = async (v) => {
    const pick = { batch_index: v.batch_index, take_index: v.take_index };
    setSelected(pick);
    // A take promoted to the main pick is no longer a $17 add-on
    setExtraTakes((xs) => xs.filter((x) => !(x.batch_index === v.batch_index && x.take_index === v.take_index)));
    try {
      await api.post(`/sites/${site.site_key}/select`, pick);
    } catch (e) {
      toast.error("Couldn't save that pick — your favorite is still safe on screen.");
    }
    if (window.innerWidth < 1024 && offerRef.current) {
      setTimeout(() => offerRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  };

  const toggleAdd = (v) => {
    setExtraTakes((xs) => {
      const has = xs.some((x) => x.batch_index === v.batch_index && x.take_index === v.take_index);
      return has
        ? xs.filter((x) => !(x.batch_index === v.batch_index && x.take_index === v.take_index))
        : [...xs, { batch_index: v.batch_index, take_index: v.take_index }];
    });
  };

  const cutMoreFree = async () => {
    setCutting(true);
    try {
      const { data } = await api.post(`/sites/${site.site_key}/batches`, {
        feedback: feedback.trim() || null,
        custom_lyrics: editedLyrics() || null,
        origin: window.location.origin,
      });
      navigate(`/recording-studio/${data.job_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The studio hiccuped — try again.");
      setCutting(false);
    }
  };

  const buyMoreTakes = async () => {
    setCutting(true);
    try {
      const { data } = await api.post(`/sites/${site.site_key}/batches/checkout`, {
        feedback: feedback.trim() || null,
        custom_lyrics: editedLyrics() || null,
        origin_url: window.location.origin,
        current_job_id: jobId,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout didn't open — try again.");
      setCutting(false);
    }
  };

  const checkout = async () => {
    if (!selected) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/intent", {
        lookup_key: "jingle_lifetime",
        origin_url: window.location.origin,
        site_key: site.site_key,
        job_id: jobId,
        selected_take: selected,
        order_bump: bump,
        extra_takes: extraTakes,
      });
      if (data.free_unlock) {
        toast.success("Your preview credits covered it — unlocking now!");
        await fetchSite(site.site_key);
        setCheckingOut(false);
        return;
      }
      navigate(`/checkout?pi=${encodeURIComponent(data.session_id)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout didn't open — try again.");
      setCheckingOut(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <p className="font-display text-2xl font-bold mb-4">We lost that tape.</p>
            <Link to="/" data-testid="preview-back-home" className="text-gold underline">Cut a new jingle</Link>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const versions = site?.versions ?? [];
  const batches = site?.batches ?? [];
  const lyrics = batches.length
    ? (batches[Math.min(activeBatch, batches.length - 1)]?.lyrics ?? site?.lyrics ?? job?.lyrics ?? [])
    : (site?.lyrics ?? job?.lyrics ?? []);
  const currentLyricsText = lyrics.join("\n").trim();
  const editedLyrics = () => {
    if (!showLyricEditor) return "";
    const draft = lyricsDraft.trim();
    return draft && draft !== currentLyricsText ? draft : "";
  };
  const selVersion = selected ? versions.find((v) => v.batch_index === selected.batch_index && v.take_index === selected.take_index) : null;
  const takeName = selVersion ? `${selVersion.name} · ${selVersion.style}` : null;
  const paid = site?.paid;
  const credits = site?.credits_cents ?? 0;
  const freeLeft = site?.free_batches_remaining ?? 0;
  const anyRecording = batches.some((b) => b.status === "recording");

  return (
    <div data-testid="jingle-preview-page" className="min-h-screen bg-obsidian text-white relative overflow-x-clip flex flex-col">
      <div className="absolute -left-40 top-40 opacity-25 pointer-events-none hidden lg:block">
        <Vinyl size={380} spinning />
      </div>

      <header className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-8 pt-10 flex items-center justify-between">
        <Link to="/" data-testid="preview-logo" className="font-display font-extrabold tracking-tight">
          <span className="text-gold">Local</span>Business<span className="text-gold">Jingle</span><span className="text-slate-500">.com</span>
        </Link>
        <div data-testid="track-ready-badge" className="flex items-center gap-2.5 glass-panel rounded-full px-4 py-2">
          <BadgeCheck className="h-4 w-4 text-emerald-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
            {versions.length} take{versions.length === 1 ? "" : "s"} ready
          </span>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-8 pt-6 sm:pt-8 pb-20 flex-1">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <h1 data-testid="preview-headline" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl">
            {site?.business_name ? <><span className="text-neon">{site.business_name}</span>, the studio sessions.</> : "Your studio session."}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl">
            Press play on the takes, pick the one that gets stuck in YOUR head — that's
            the one your customers won't escape either. Want more options? Keep reading.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 mt-8 sm:mt-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="lg:col-span-7 space-y-10 order-1"
          >
            {batches.length > 0 && (() => {
              const batch = batches[Math.min(activeBatch, batches.length - 1)];
              const takes = versions
                .filter((v) => v.batch_index === batch.batch_index)
                .sort((a, b) => {
                  // The picked take slides to the top of the list
                  const aSel = selected && a.batch_index === selected.batch_index && a.take_index === selected.take_index;
                  const bSel = selected && b.batch_index === selected.batch_index && b.take_index === selected.take_index;
                  return (bSel ? 1 : 0) - (aSel ? 1 : 0);
                });
              const total = batches.length;
              return (
                <section data-testid="batch-carousel" className="glass-panel rounded-3xl p-5 sm:p-7">
                  <h2 data-testid="versions-headline" className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-snug mb-4">
                    Which version do you want to download <span className="text-gold">without the watermark?</span>
                  </h2>
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div className="flex items-center gap-3 min-w-0 flex-wrap pt-2">
                      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
                        {BATCH_NAMES[batch.batch_index] ?? `Batch ${batch.batch_index + 1}`}
                      </span>
                      {batch.feedback && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neon border border-neon/30 rounded-full px-2.5 py-1">
                          Direction: "{batch.feedback}"
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0">
                      {total > 1 && (
                        <>
                          <span data-testid="batch-counter" className="font-mono text-[10px] text-slate-500 tracking-[0.2em] mr-1">
                            {Math.min(activeBatch, total - 1) + 1} / {total}
                          </span>
                          <button
                            data-testid="batch-prev-btn"
                            onClick={() => setActiveBatch((i) => Math.max(0, i - 1))}
                            disabled={activeBatch === 0}
                            aria-label="Previous batch of takes"
                            className="h-11 w-11 sm:h-12 sm:w-12 rounded-full border-2 border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black active:scale-90 transition-all cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gold"
                          >
                            <ChevronLeft className="h-6 w-6" />
                          </button>
                          <button
                            data-testid="batch-next-btn"
                            onClick={() => setActiveBatch((i) => Math.min(total - 1, i + 1))}
                            disabled={activeBatch >= total - 1}
                            aria-label="Next batch of takes"
                            className="h-11 w-11 sm:h-12 sm:w-12 rounded-full border-2 border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black active:scale-90 transition-all cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gold"
                          >
                            <ChevronRight className="h-6 w-6" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={batch.batch_index}
                      initial={{ opacity: 0, x: 48 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -48 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {batch.status === "recording" ? (
                        <div data-testid={`batch-recording-${batch.batch_index}`} className="rounded-2xl border border-white/10 bg-obsidian/60 p-8">
                          <div className="flex items-center gap-3 mb-4">
                            <Loader2 className="h-5 w-5 text-gold animate-spin" />
                            <p className="font-display font-bold text-lg">Takes {batch.batch_index * 3 + 1}–{batch.batch_index * 3 + 3} are cutting now...</p>
                          </div>
                          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-gold to-coral transition-[width] duration-700" style={{ width: `${batch.progress}%` }} />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {takes.map((v) => (
                            <motion.div
                              key={v.take_number}
                              layout
                              transition={{ layout: { type: "spring", stiffness: 120, damping: 17 } }}
                            >
                              <PlaylistRow
                                siteKey={site.site_key}
                                take={v}
                                songTitle={site.song_title}
                                bust={batch.job_id}
                                selected={!!selected && selected.batch_index === v.batch_index && selected.take_index === v.take_index}
                                added={extraTakes.some((x) => x.batch_index === v.batch_index && x.take_index === v.take_index)}
                                anySelected={!!selected}
                                paid={paid}
                                onSelect={select}
                                onToggleAdd={toggleAdd}
                              />
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                  {total > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-5">
                      {batches.map((b, i) => (
                        <button
                          key={b.batch_index}
                          data-testid={`batch-dot-${i}`}
                          onClick={() => setActiveBatch(i)}
                          aria-label={`Go to batch ${i + 1}`}
                          className={`h-2 rounded-full transition-all cursor-pointer ${i === activeBatch ? "w-7 bg-gold" : "w-2 bg-white/20 hover:bg-white/40"}`}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })()}
          </motion.div>

          {/* Offer card — on mobile this sits right under the takes, above the lyrics */}
          <motion.div
            ref={offerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="lg:col-span-5 lg:row-span-2 scroll-mt-24 lg:sticky lg:top-6 self-start order-2"
          >
            {paid ? (
              <div className="rounded-3xl border border-emerald-400/40 bg-ink p-8 text-center shadow-[0_0_60px_rgba(16,185,129,0.12)]">
                <BadgeCheck className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
                <p className="font-display text-2xl font-bold mb-2">It's official — this jingle is yours.</p>
                <p className="text-slate-400 text-sm">Your master files are unlocked below. Go make your city hum.</p>
              </div>
            ) : selected ? (
              <OfferStackCard
                onCheckout={checkout}
                loading={checkingOut}
                bumpChecked={bump}
                onBumpToggle={() => setBump((b) => !b)}
                takeName={takeName}
                creditsCents={credits}
                extrasCount={extraTakes.length}
                testIdPrefix="preview-offer"
              />
            ) : (
              <div data-testid="preview-offer-placeholder" className="rounded-3xl border border-dashed border-white/15 bg-ink/60 p-8 text-center">
                <Lock className="h-8 w-8 text-slate-600 mx-auto mb-4" />
                <p className="font-display text-xl font-bold mb-2">Your download options appear here.</p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Press play on the takes, then hit <span className="text-gold font-semibold">"Pick this take"</span> on
                  the one that gets stuck in your head.
                </p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="lg:col-span-7 space-y-10 order-3"
          >
            {/* More takes — never punitive, always an expansion of choice */}
            {!paid && !anyRecording && (
              <motion.section
                data-testid="more-takes-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.6 }}
                className="rounded-3xl border border-neon/30 bg-ink p-6 sm:p-8"
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <AudioWaveform className="h-5 w-5 text-neon" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-neon">More options</p>
                </div>
                {freeLeft > 0 ? (
                  <>
                    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-2">
                      Not quite it? The next batch is on the house.
                    </h2>
                    <p className="text-sm text-slate-400 leading-relaxed mb-5">
                      Every website gets 6 free studio takes — you've used {site?.batches_used * 3}.
                      Tell the studio what to change, or give the studio freedom to try again undirected,
                      and we'll cut 3 fresh takes.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-2">
                      Want more options? Generate 3 more for $3.
                    </h2>
                    <p className="text-sm text-slate-400 leading-relaxed mb-5">
                      Keep exploring — every $3 batch is banked as preview credit and comes
                      off your master automatically when you pick a favorite.
                      {credits > 0 && (
                        <span className="text-emerald-400 font-semibold"> You've banked ${(credits / 100).toFixed(0)} so far.</span>
                      )}
                    </p>
                  </>
                )}
                <div className="space-y-4 mb-5">
                  <div>
                    <button
                      type="button"
                      data-testid="edit-lyrics-toggle"
                      onClick={() => {
                        const open = !showLyricEditor;
                        setShowLyricEditor(open);
                        if (open && !lyricsDraft.trim()) setLyricsDraft(currentLyricsText);
                      }}
                      className="flex items-center gap-2 text-sm font-semibold text-gold hover:text-neon transition-colors cursor-pointer"
                    >
                      <PenLine className="h-4 w-4" />
                      {showLyricEditor ? "Use the studio's lyrics instead" : "Want to edit the lyrics yourself?"}
                    </button>
                    {showLyricEditor && (
                      <div className="mt-3">
                        {batches.filter((b) => (b.lyrics || []).length > 0).length > 1 && (
                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 mr-1">
                              Start from:
                            </span>
                            {batches.filter((b) => (b.lyrics || []).length > 0).map((b) => (
                              <button
                                key={b.batch_index}
                                type="button"
                                data-testid={`lyrics-version-pill-${b.batch_index}`}
                                onClick={() => {
                                  setActiveBatch(b.batch_index);
                                  const text = (b.lyrics || []).join("\n").trim();
                                  if (text) {
                                    setLyricsDraft(text);
                                    draftSource.current = text;
                                  }
                                }}
                                className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors cursor-pointer ${
                                  b.batch_index === Math.min(activeBatch, batches.length - 1)
                                    ? "bg-gold text-black font-bold"
                                    : "border border-white/20 text-slate-400 hover:border-gold hover:text-gold"
                                }`}
                              >
                                {BATCH_NAMES[b.batch_index] ?? `Batch ${b.batch_index + 1}`} words
                              </button>
                            ))}
                          </div>
                        )}
                        <textarea
                          data-testid="custom-lyrics-input"
                          value={lyricsDraft}
                          onChange={(e) => setLyricsDraft(e.target.value)}
                          rows={Math.min(10, Math.max(4, lyricsDraft.split("\n").length + 1))}
                          className="w-full bg-obsidian/80 border border-gold/30 rounded-xl px-4 py-3.5 text-base font-display font-bold placeholder:text-slate-600 focus:outline-none focus:border-gold transition-colors"
                        />
                        <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                          We'll sing YOUR words in the next batch of 3 brand-new styles. Keep lines short
                          and singable — 4 to 9 syllables each works best, and end on the business name.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      data-testid="musical-direction-toggle"
                      onClick={() => setShowDirectionEditor((v) => !v)}
                      className="flex items-center gap-2 text-sm font-semibold text-gold hover:text-neon transition-colors cursor-pointer"
                    >
                      <Music className="h-4 w-4" />
                      {showDirectionEditor ? "No musical direction needed" : "Want to give more musical direction?"}
                    </button>
                    {showDirectionEditor && (
                      <div className="mt-3">
                        <textarea
                          data-testid="batch-feedback-input"
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          placeholder='"make it slower", "more country", "female vocalist vibe"...'
                          rows={2}
                          className="w-full bg-obsidian/80 border border-white/15 rounded-xl px-4 py-3.5 text-sm placeholder:text-slate-600 focus:outline-none focus:border-neon transition-colors resize-none"
                        />
                        <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                          Tell the studio what to change about the SOUND — the next 3 takes follow your direction.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {freeLeft > 0 ? (
                  <button
                    data-testid="cut-free-batch-btn"
                    onClick={cutMoreFree}
                    disabled={cutting}
                    className="w-full sm:w-auto bg-gradient-to-r from-neon to-[#00B8CC] text-black font-extrabold px-8 py-3.5 rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2.5"
                  >
                    {cutting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                    CUT 3 MORE FREE TAKES
                  </button>
                ) : (
                  <button
                    data-testid="buy-batch-btn"
                    onClick={buyMoreTakes}
                    disabled={cutting}
                    className="w-full sm:w-auto bg-gradient-to-r from-neon to-[#00B8CC] text-black font-extrabold px-8 py-3.5 rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2.5"
                  >
                    {cutting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                    GENERATE 3 MORE — $3
                  </button>
                )}
              </motion.section>
            )}

            {!paid ? (
              <div data-testid="locked-downloads" className="glass-panel rounded-2xl p-6">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 mb-4">
                  Locked until you unlock your favorite take
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {["Main Jingle (WAV)", "Short Hook", "Hold Music (add-on)"].map((label) => (
                    <button
                      key={label}
                      data-testid={`locked-${label.split(" ")[0].toLowerCase()}`}
                      onClick={() => toast.info("Pick a take and unlock the package to download this file.")}
                      className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-obsidian/60 px-4 py-3.5 text-sm text-slate-500 cursor-not-allowed"
                    >
                      <Lock className="h-4 w-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <UnlockedDownloads
                siteKey={site.site_key}
                holdMusic={site.hold_music}
                radio60={site.radio60_unlocked}
                filesReady={site.files_ready || {}}
                extraTakes={site.extra_takes || []}
              />
            )}

            <div className="glass-panel rounded-2xl p-6 flex gap-4 items-start">
              <MessageSquare className="h-7 w-7 text-neon shrink-0" />
              <div>
                <p className="font-display font-bold text-lg">The Love-It-Or-Don't-Pay Guarantee.</p>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                  Your first 6 previews are 100% free. If we’re close but you still want
                  changes after 6 tries, we’ll do each one for $1 in sets of 3, and we’ll
                  credit 100% of what you spend on previews toward the final master once you
                  get your jingle locked in. You also get a few free revisions after you
                  purchase the clean master, depending on the options you choose at checkout.
                </p>
              </div>
            </div>

            <div data-testid="watermark-note" className="flex items-start gap-2.5 max-w-2xl text-slate-500 px-1">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <p className="text-xs leading-relaxed">
                By the way: every take is a protected stream — a "PREVIEW ONLY" audio tag is
                embedded every few seconds, and downloads stay locked until you pick a take
                and unlock it. The master you buy is 100% clean.
              </p>
            </div>
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export const UnlockedDownloads = ({ siteKey, sessionId, holdMusic = false, radio60 = false, filesReady = {}, extraTakes = [] }) => {
  const files = [
    { version: "master30", label: "Main Jingle", fmt: "WAV" },
    { version: "cut15", label: "Short Hook", fmt: "WAV" },
    { version: "license", label: "Commercial License", fmt: "PDF" },
  ];
  if (holdMusic) files.push({ version: "holdmusic", label: "Instrumental Hold Music", fmt: "WAV" });
  if (radio60) files.push({ version: "radio60", label: "Extended Radio Edition", fmt: "WAV" });
  if (radio60 && filesReady.radiodonut)
    files.push({ version: "radiodonut", label: "Music-Only Donut — no voice-over (surprise bonus)", fmt: "WAV" });
  extraTakes.forEach((t) =>
    files.push({ version: `take_${t.batch_index}_${t.take_index}`, label: `Take ${t.take_number} (Bonus)`, fmt: "WAV" })
  );
  const pending = files.filter((f) => !filesReady[f.version]);
  return (
    <div data-testid="unlocked-downloads" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-6">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400 mb-4">
        Your master files — unwatermarked, full quality
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {files.map((f) => (
          filesReady[f.version] ? (
            <a
              key={f.version}
              data-testid={`download-${f.version}`}
              href={siteDownloadUrl(siteKey, f.version, sessionId)}
              download
              className="flex items-center justify-between gap-2 rounded-xl border border-emerald-400/30 bg-obsidian/70 px-4 py-3.5 text-sm text-slate-200 hover:border-emerald-400 hover:bg-emerald-400/10 transition-colors"
            >
              <span>{f.label} <span className="text-slate-500">· {f.fmt}</span></span>
              <Download className="h-4 w-4 text-emerald-400" />
            </a>
          ) : (
            <div
              key={f.version}
              data-testid={`download-${f.version}-pending`}
              className="flex items-center justify-between gap-2 rounded-xl border border-amber-400/30 bg-obsidian/70 px-4 py-3.5 text-sm text-slate-400"
            >
              <span>{f.label} <span className="text-slate-500">· {f.fmt}</span></span>
              <span className="flex items-center gap-1.5 text-xs text-amber-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> In the studio
              </span>
            </div>
          )
        ))}
      </div>
      {pending.length > 0 && (
        <p data-testid="downloads-pending-note" className="mt-4 text-xs text-amber-300/80 leading-relaxed">
          Still mastering your clean tracks — they replace themselves here automatically,
          usually within a couple of minutes. No need to refresh.
        </p>
      )}
    </div>
  );
};
